/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   getFittedZoom
 */

import type { MaybeEmpty } from '@zcode/utility-types'
import { TILE_SIZE } from '../../constants'
import type { AutocompleteBoxType, BoundsObj, Dimensions, MaybeNullBoundsObj } from '../../types'
import { createMercator } from './createMercator'

interface GetAdjustedParams {
  base: number
  ratios: number[]
  allowFloat?: boolean
}

const getAdjusted = ({ base, ratios, allowFloat }: GetAdjustedParams) => {
  const adjusted = Math.min(
    base - Math.log(ratios[0]) / Math.log(2),
    base - Math.log(ratios[1]) / Math.log(2)
  )

  return allowFloat ? adjusted : Math.round(adjusted)
}

interface GetFittedZoomParams {
  bounds?: AutocompleteBoxType | MaybeEmpty<BoundsObj> | MaybeNullBoundsObj | null
  dimensions: Dimensions
  minZoom?: number
  maxZoom?: number
  tileSize?: number
  allowFloat?: boolean
}

export const getFittedZoom = ({
  bounds,
  dimensions,
  minZoom = 0,
  maxZoom = 22,
  tileSize = TILE_SIZE,
  allowFloat,
}: GetFittedZoomParams) => {
  if (!bounds) {
    return maxZoom
  }

  const { minLng, minLat, maxLng, maxLat } = bounds

  if (!minLat || !minLng || !maxLng || !maxLat) {
    return maxZoom
  }

  const merc = createMercator(tileSize)
  const base = maxZoom
  const [mercMinLat, mercMinLng] = merc.px([minLng, minLat], base)
  const [mercMaxLat, mercMaxLng] = merc.px([maxLng, maxLat], base)
  const width = Math.abs(mercMaxLat - mercMinLat)
  const height = Math.abs(mercMaxLng - mercMinLng)
  const ratios = [width / (dimensions?.width ?? 1), height / (dimensions?.height ?? 1)]
  const adjusted = getAdjusted({ base, ratios, allowFloat })
  const zoom = Math.max(minZoom, Math.min(maxZoom, adjusted))

  return zoom
}
